import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900" }
const _hoisted_2 = {
  key: 3,
  class: "container mx-auto px-3 py-2 sm:px-4 sm:py-3 max-w-7xl"
}
const _hoisted_3 = { class: "block lg:hidden mb-4" }
const _hoisted_4 = { class: "flex items-center justify-between mb-3" }
const _hoisted_5 = { class: "flex items-center gap-2" }
const _hoisted_6 = ["fill"]
const _hoisted_7 = { class: "text-center" }
const _hoisted_8 = { class: "text-xl font-bold text-gray-900 dark:text-white mb-1" }
const _hoisted_9 = { class: "flex items-center justify-center gap-4 text-sm text-gray-500 dark:text-gray-400" }
const _hoisted_10 = { class: "flex items-center gap-1" }
const _hoisted_11 = { class: "flex items-center gap-1" }
const _hoisted_12 = { class: "hidden lg:block" }
const _hoisted_13 = { class: "block lg:hidden" }
const _hoisted_14 = { class: "mb-4" }
const _hoisted_15 = { class: "space-y-3" }
const _hoisted_16 = { class: "bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm" }
const _hoisted_17 = { class: "flex overflow-x-auto gap-2 pb-2 scrollbar-hide" }
const _hoisted_18 = ["onClick"]
const _hoisted_19 = { class: "bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm" }
const _hoisted_20 = { class: "grid grid-cols-2 gap-2" }
const _hoisted_21 = ["onClick", "disabled"]
const _hoisted_22 = { class: "text-left" }
const _hoisted_23 = { class: "text-xs font-medium text-gray-900 dark:text-white" }
const _hoisted_24 = { class: "text-xs text-gray-500 dark:text-gray-400" }
const _hoisted_25 = {
  key: 0,
  class: "bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm"
}
const _hoisted_26 = { class: "text-sm text-gray-600 dark:text-gray-400 leading-relaxed" }
const _hoisted_27 = {
  key: 1,
  class: "bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm"
}
const _hoisted_28 = { class: "flex flex-wrap gap-2" }
const _hoisted_29 = { class: "hidden lg:grid lg:grid-cols-4 gap-4" }
const _hoisted_30 = { class: "lg:col-span-1 space-y-3" }
const _hoisted_31 = { class: "lg:col-span-3" }
const _hoisted_32 = {
  key: 0,
  class: "bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm mt-4"
}
const _hoisted_33 = { class: "text-sm text-gray-600 dark:text-gray-400 leading-relaxed" }
const _hoisted_34 = {
  key: 1,
  class: "bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm mt-4"
}
const _hoisted_35 = { class: "flex flex-wrap gap-2" }

import ImageViewer from '../components/ImageViewer.vue';
import DownloadNotification from '../components/wallpaper/DownloadNotification.vue';
import LoadingState from '../components/common/LoadingState.vue';
import ErrorState from '../components/common/ErrorState.vue';

// New Modular Components
import WallpaperHeader from '../components/wallpaper-detail/WallpaperHeader.vue';
import DeviceSelector from '../components/wallpaper-detail/DeviceSelector.vue';
import VariationNavigator from '../components/wallpaper-detail/VariationNavigator.vue';
import DownloadPanel from '../components/wallpaper-detail/DownloadPanel.vue';
import DevicePreview from '../components/wallpaper-detail/DevicePreview.vue';
import DownloadLimitIndicator from '../components/ui/DownloadLimitIndicator.vue';
import AuthModal from '../components/AuthModal.vue';
import MobileAd from '../components/MobileAd.vue';
import MobileInterstitialAd from '../components/MobileInterstitialAd.vue';
import InterstitialAdModal from '../components/InterstitialAdModal.vue';
import MobileUtils from '../utils/mobileUtils';


// Vue imports
import { ref, onMounted, computed, watch } from 'vue';

// Composables
import { useWallpaperDetail } from '../composables/useWallpaperDetail';

// Services
import { centralizedImageSecurity } from '@/services/centralizedImageSecurity';

// Use the wallpaper detail composable
type DownloadResolution = Parameters<typeof downloadWallpaper>[0];

// Override downloadWallpaper to show interstitial ad

export default /*@__PURE__*/_defineComponent({
  __name: 'WallpaperDetail',
  setup(__props) {

// Components
const {
  // State
  wallpaper,
  loading,
  error,
  isDownloading,
  showImageViewer,
  selectedDevice,
  notification,
  isFavorited,

  // Computed
  categoryName,
  availableDevices,
  deviceInfo,
  currentImageUrl,
  currentDimensions,
  currentVariationIndex,

  // Methods
  getDeviceDisplayName,
  getDeviceVariations,
  calculateAspectRatio,
  mapToDevicePreviewType,
  navigateToPreviousVariation,
  navigateToNextVariation,
  handleVariationSelection,
  handleDeviceSelection,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
  getAvailableResolutions,
  downloadWallpaper,
  shareWallpaper,
  toggleFavorite,
  hideNotification,
  goBack
} = useWallpaperDetail();

// Wallpaper Fit Options State (always default to 'fill')
const selectedFitOption = ref('fill');

// Download limit state
const downloadLimitInfo = ref({
  remaining: 3,
  total: 3,
  isAuthenticated: false
});

// Auth modal state
const showAuthModal = ref(false);

// Interstitial ad state
const showInterstitialAd = ref(false);
const pendingDownload = ref<{name: string; size: string} | null>(null);
const originalDownloadFunction = ref<(() => void) | null>(null);

// Computed properties for download functionality
const canDownload = computed(() => {
  const info = centralizedImageSecurity.getRemainingDownloads();
  const canDownloadResult = info.isAuthenticated || info.remaining > 0;
  
  // Log download state for debugging
  if (process.env.NODE_ENV === 'development') {
    console.log('[WallpaperDetail] Download state check:', {
      isAuthenticated: info.isAuthenticated,
      remaining: info.remaining,
      total: info.total,
      canDownload: canDownloadResult
    });
  }
  
  return canDownloadResult;
});



// Update download limit info
const updateDownloadLimitInfo = () => {
  downloadLimitInfo.value = centralizedImageSecurity.getRemainingDownloads();
};

// Handle successful authentication
const handleAuthSuccess = (user: { uid: string; email?: string | null }) => {
  showAuthModal.value = false;
  updateDownloadLimitInfo();
  console.log('Authentication successful:', user);
};

// Wallpaper Fit Options Methods
const handleFitChanged = (fitOption: string) => {
  // Always force Fill so the preview consistently fills the frame
  selectedFitOption.value = 'fill';
  localStorage.setItem('wallpaper-fit-preference', 'fill');
  console.log('Fit option forced to: fill (requested:', fitOption, ')');
};

// Mobile interstitial ad reference
const mobileInterstitialRef = ref<InstanceType<typeof MobileInterstitialAd> | null>(null);
const downloadWallpaperWithAd = async (resolution: DownloadResolution) => {
  // Add haptic feedback for mobile
  if (MobileUtils.isNative()) {
    await MobileUtils.hapticFeedback('light');
  }

  // Try to show mobile interstitial ad first
  if (MobileUtils.isNative() && mobileInterstitialRef.value) {
    mobileInterstitialRef.value.incrementTrigger();
    
    if (mobileInterstitialRef.value.canShowAd) {
      const adShown = await mobileInterstitialRef.value.showInterstitialAd();
      if (adShown) {
        // Ad was shown, proceed with download after ad closes
          setTimeout(() => downloadWallpaper(resolution), 1000);
        return;
      }
    }
  }

  // Fallback to web interstitial modal for web or if mobile ad fails
  if (!MobileUtils.isNative()) {
    // Store the original download function
    originalDownloadFunction.value = () => downloadWallpaper(resolution);
    
    // Set pending download info
    pendingDownload.value = {
      name: `${wallpaper.value?.title || 'Wallpaper'} - ${resolution.name}`,
      size: resolution.size
    };
    
    // Show interstitial ad modal
    showInterstitialAd.value = true;
  } else {
    // Direct download for mobile if no ad shown
    downloadWallpaper(resolution);
  }
};

// Interstitial Ad Methods
const handleInterstitialDownload = () => {
  if (originalDownloadFunction.value) {
    originalDownloadFunction.value();
    originalDownloadFunction.value = null;
  }
  showInterstitialAd.value = false;
  pendingDownload.value = null;
};

const handleInterstitialSkip = () => {
  if (originalDownloadFunction.value) {
    originalDownloadFunction.value();
    originalDownloadFunction.value = null;
  }
  showInterstitialAd.value = false;
  pendingDownload.value = null;
};



// Initialize fit option as Fill on load
onMounted(() => {
  selectedFitOption.value = 'fill';
  localStorage.setItem('wallpaper-fit-preference', 'fill');
});

// If wallpaper loads later and preference was 'fill', switch to 'fit' for packs to avoid stretching
watch(() => wallpaper.value?.isWallpaperPack, (isPack) => {
  if (isPack && selectedFitOption.value !== 'fill') {
    selectedFitOption.value = 'fill';
  }
});

// Avoid implicit any in template expressions by defining typed handlers
const onSelectVariationMobile = (index: number) => {
  if (selectedDevice.value) {
    handleVariationSelection(selectedDevice.value, index)
  }
}
const onSelectVariationDesktop = (index: number) => {
  if (selectedDevice.value) {
    handleVariationSelection(selectedDevice.value, index)
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_unref(showImageViewer))
      ? (_openBlock(), _createBlock(ImageViewer, {
          key: 0,
          src: _unref(wallpaper)?.videoUrl || _unref(currentImageUrl),
          "wallpaper-id": _unref(wallpaper)?.id || '',
          alt: `${_unref(wallpaper)?.title || 'Wallpaper'} - ${_unref(getDeviceDisplayName)(_unref(selectedDevice))}`,
          onClose: _cache[0] || (_cache[0] = ($event: any) => (showImageViewer.value = false))
        }, null, 8, ["src", "wallpaper-id", "alt"]))
      : _createCommentVNode("", true),
    (_unref(loading))
      ? (_openBlock(), _createBlock(LoadingState, { key: 1 }))
      : (_unref(error))
        ? (_openBlock(), _createBlock(ErrorState, {
            key: 2,
            error: _unref(error),
            onBackToHome: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$router.push('/')))
          }, null, 8, ["error"]))
        : (_unref(wallpaper))
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("button", {
                    onClick: _cache[2] || (_cache[2] = 
//@ts-ignore
(...args) => (_unref(goBack) && _unref(goBack)(...args))),
                    class: "flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                  }, _cache[13] || (_cache[13] = [
                    _createElementVNode("svg", {
                      class: "w-5 h-5 mr-1",
                      fill: "none",
                      stroke: "currentColor",
                      viewBox: "0 0 24 24"
                    }, [
                      _createElementVNode("path", {
                        "stroke-linecap": "round",
                        "stroke-linejoin": "round",
                        "stroke-width": "2",
                        d: "M15 19l-7-7 7-7"
                      })
                    ], -1),
                    _createTextVNode(" Back ", -1)
                  ])),
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("button", {
                      onClick: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(toggleFavorite) && _unref(toggleFavorite)(...args))),
                      class: _normalizeClass([
              'p-2 rounded-full transition-all duration-200',
              _unref(isFavorited)
                ? 'bg-red-100 text-red-500 dark:bg-red-900/30 dark:text-red-400'
                : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
            ])
                    }, [
                      (_openBlock(), _createElementBlock("svg", {
                        class: "w-5 h-5",
                        fill: _unref(isFavorited) ? 'currentColor' : 'none',
                        stroke: "currentColor",
                        viewBox: "0 0 24 24"
                      }, _cache[14] || (_cache[14] = [
                        _createElementVNode("path", {
                          "stroke-linecap": "round",
                          "stroke-linejoin": "round",
                          "stroke-width": "2",
                          d: "M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                        }, null, -1)
                      ]), 8, _hoisted_6))
                    ], 2),
                    _createElementVNode("button", {
                      onClick: _cache[4] || (_cache[4] = 
//@ts-ignore
(...args) => (_unref(shareWallpaper) && _unref(shareWallpaper)(...args))),
                      class: "p-2 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    }, _cache[15] || (_cache[15] = [
                      _createElementVNode("svg", {
                        class: "w-5 h-5",
                        fill: "none",
                        stroke: "currentColor",
                        viewBox: "0 0 24 24"
                      }, [
                        _createElementVNode("path", {
                          "stroke-linecap": "round",
                          "stroke-linejoin": "round",
                          "stroke-width": "2",
                          d: "M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
                        })
                      ], -1)
                    ]))
                  ])
                ]),
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("h1", _hoisted_8, _toDisplayString(_unref(wallpaper).title), 1),
                  _createElementVNode("div", _hoisted_9, [
                    _createElementVNode("span", _hoisted_10, [
                      _cache[16] || (_cache[16] = _createElementVNode("svg", {
                        class: "w-4 h-4",
                        fill: "none",
                        stroke: "currentColor",
                        viewBox: "0 0 24 24"
                      }, [
                        _createElementVNode("path", {
                          "stroke-linecap": "round",
                          "stroke-linejoin": "round",
                          "stroke-width": "2",
                          d: "M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                        })
                      ], -1)),
                      _createTextVNode(" " + _toDisplayString(_unref(categoryName)), 1)
                    ]),
                    _createElementVNode("span", _hoisted_11, [
                      _cache[17] || (_cache[17] = _createElementVNode("svg", {
                        class: "w-4 h-4",
                        fill: "none",
                        stroke: "currentColor",
                        viewBox: "0 0 24 24"
                      }, [
                        _createElementVNode("path", {
                          "stroke-linecap": "round",
                          "stroke-linejoin": "round",
                          "stroke-width": "2",
                          d: "M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        })
                      ], -1)),
                      _createTextVNode(" " + _toDisplayString(_unref(wallpaper).downloadCount || 0) + " downloads ", 1)
                    ])
                  ])
                ])
              ]),
              _createElementVNode("div", _hoisted_12, [
                _createVNode(WallpaperHeader, {
                  title: _unref(wallpaper).title,
                  "category-name": _unref(categoryName),
                  "download-count": _unref(wallpaper).downloadCount || 0,
                  "is-wallpaper-pack": !!_unref(wallpaper).isWallpaperPack,
                  "is-live-wallpaper": !!(_unref(wallpaper).isVideo || _unref(wallpaper).isAnimated),
                  "show-quick-download": false,
                  "is-downloading": false,
                  onBack: _unref(goBack)
                }, null, 8, ["title", "category-name", "download-count", "is-wallpaper-pack", "is-live-wallpaper", "onBack"])
              ]),
              _createElementVNode("div", _hoisted_13, [
                _createElementVNode("div", _hoisted_14, [
                  _createVNode(DevicePreview, {
                    "device-type": _unref(mapToDevicePreviewType)(_unref(selectedDevice)),
                    "device-display-name": _unref(getDeviceDisplayName)(_unref(selectedDevice)),
                    "image-url": _unref(currentImageUrl) || '',
                    "wallpaper-id": _unref(wallpaper)?.id || '',
                    dimensions: _unref(currentDimensions),
                    "aspect-ratio": _unref(calculateAspectRatio)(_unref(currentDimensions)),
                    "has-multiple-variations": !!(_unref(wallpaper)?.isWallpaperPack && _unref(selectedDevice) && _unref(getDeviceVariations)(_unref(wallpaper), _unref(selectedDevice)).length > 1),
                    variations: _unref(wallpaper)?.isWallpaperPack && _unref(selectedDevice) ? _unref(getDeviceVariations)(_unref(wallpaper), _unref(selectedDevice)) : [],
                    "current-variation-index": _unref(currentVariationIndex),
                    "total-variations": _unref(wallpaper)?.isWallpaperPack && _unref(selectedDevice) ? _unref(getDeviceVariations)(_unref(wallpaper), _unref(selectedDevice)).length : 0,
                    "fit-option": selectedFitOption.value,
                    "is-loading": false,
                    "is-live-wallpaper": _unref(wallpaper)?.isVideo || _unref(wallpaper)?.isAnimated,
                    "is-video": _unref(wallpaper)?.isVideo,
                    "is-animated": _unref(wallpaper)?.isAnimated,
                    "video-url": _unref(wallpaper)?.videoUrl,
                    onFullscreen: _cache[5] || (_cache[5] = ($event: any) => (showImageViewer.value = true)),
                    onShare: _unref(shareWallpaper),
                    onPreviewClick: _cache[6] || (_cache[6] = ($event: any) => (showImageViewer.value = true)),
                    onTouchStart: _unref(handleTouchStart),
                    onTouchMove: _unref(handleTouchMove),
                    onTouchEnd: _unref(handleTouchEnd),
                    onSelectVariation: onSelectVariationMobile,
                    onFitChanged: handleFitChanged
                  }, null, 8, ["device-type", "device-display-name", "image-url", "wallpaper-id", "dimensions", "aspect-ratio", "has-multiple-variations", "variations", "current-variation-index", "total-variations", "fit-option", "is-live-wallpaper", "is-video", "is-animated", "video-url", "onShare", "onTouchStart", "onTouchMove", "onTouchEnd"])
                ]),
                _createElementVNode("div", _hoisted_15, [
                  _createElementVNode("div", _hoisted_16, [
                    _cache[18] || (_cache[18] = _createElementVNode("h3", { class: "text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" }, "Device", -1)),
                    _createElementVNode("div", _hoisted_17, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(availableDevices), (device) => {
                        return (_openBlock(), _createElementBlock("button", {
                          key: device,
                          onClick: ($event: any) => (_unref(handleDeviceSelection)(device)),
                          class: _normalizeClass([
                'flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200',
                _unref(selectedDevice) === device
                  ? 'bg-blue-500 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              ])
                        }, _toDisplayString(_unref(getDeviceDisplayName)(device)), 11, _hoisted_18))
                      }), 128))
                    ])
                  ]),
                  _createVNode(MobileAd, {
                    "ad-type": "rectangle",
                    class: "mb-3"
                  }),
                  _createVNode(DownloadLimitIndicator, {
                    compact: true,
                    onCreateAccount: _cache[7] || (_cache[7] = ($event: any) => (showAuthModal.value = true)),
                    onDownloadLimitReached: updateDownloadLimitInfo
                  }),
                  _createElementVNode("div", _hoisted_19, [
                    _cache[20] || (_cache[20] = _createElementVNode("h3", { class: "text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" }, "Downloads", -1)),
                    _createElementVNode("div", _hoisted_20, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(getAvailableResolutions)().slice(0, 4), (resolution, index) => {
                        return (_openBlock(), _createElementBlock("button", {
                          key: index,
                          onClick: ($event: any) => (downloadWallpaperWithAd(resolution)),
                          disabled: _unref(isDownloading) || !canDownload.value,
                          class: _normalizeClass([
                  'flex items-center justify-between p-2 rounded-lg transition-colors',
                  canDownload.value 
                    ? 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50'
                    : 'bg-gray-200 dark:bg-gray-600 cursor-not-allowed opacity-50'
                ])
                        }, [
                          _createElementVNode("div", _hoisted_22, [
                            _createElementVNode("div", _hoisted_23, _toDisplayString(resolution.name), 1),
                            _createElementVNode("div", _hoisted_24, _toDisplayString(resolution.size), 1)
                          ]),
                          _cache[19] || (_cache[19] = _createElementVNode("svg", {
                            class: "w-4 h-4 text-gray-400",
                            fill: "none",
                            stroke: "currentColor",
                            viewBox: "0 0 24 24"
                          }, [
                            _createElementVNode("path", {
                              "stroke-linecap": "round",
                              "stroke-linejoin": "round",
                              "stroke-width": "2",
                              d: "M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            })
                          ], -1))
                        ], 10, _hoisted_21))
                      }), 128))
                    ])
                  ]),
                  (_unref(wallpaper).description)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                        _cache[21] || (_cache[21] = _createElementVNode("h3", { class: "text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" }, "Description", -1)),
                        _createElementVNode("p", _hoisted_26, _toDisplayString(_unref(wallpaper).description), 1)
                      ]))
                    : _createCommentVNode("", true),
                  (_unref(wallpaper).tags && _unref(wallpaper).tags.length > 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
                        _cache[22] || (_cache[22] = _createElementVNode("h3", { class: "text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" }, "Tags", -1)),
                        _createElementVNode("div", _hoisted_28, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(wallpaper).tags, (tag) => {
                            return (_openBlock(), _createElementBlock("span", {
                              key: tag,
                              class: "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                            }, " #" + _toDisplayString(tag), 1))
                          }), 128))
                        ])
                      ]))
                    : _createCommentVNode("", true)
                ])
              ]),
              _createElementVNode("div", _hoisted_29, [
                _createElementVNode("div", _hoisted_30, [
                  _createVNode(DeviceSelector, {
                    "available-devices": _unref(availableDevices),
                    "selected-device": _unref(selectedDevice),
                    "device-info": _unref(deviceInfo),
                    "is-wallpaper-pack": _unref(wallpaper).isWallpaperPack,
                    onDeviceSelected: _unref(handleDeviceSelection)
                  }, null, 8, ["available-devices", "selected-device", "device-info", "is-wallpaper-pack", "onDeviceSelected"]),
                  (_unref(wallpaper).isWallpaperPack && _unref(selectedDevice) && _unref(getDeviceVariations)(_unref(wallpaper), _unref(selectedDevice)).length > 1)
                    ? (_openBlock(), _createBlock(VariationNavigator, {
                        key: 0,
                        "current-index": _unref(currentVariationIndex),
                        "total-variations": _unref(getDeviceVariations)(_unref(wallpaper), _unref(selectedDevice)).length,
                        variations: _unref(getDeviceVariations)(_unref(wallpaper), _unref(selectedDevice)),
                        onPrevious: _unref(navigateToPreviousVariation),
                        onNext: _unref(navigateToNextVariation),
                        onSelectVariation: onSelectVariationDesktop
                      }, null, 8, ["current-index", "total-variations", "variations", "onPrevious", "onNext"]))
                    : _createCommentVNode("", true),
                  _createVNode(MobileAd, { "ad-type": "rectangle" }),
                  _createVNode(DownloadLimitIndicator, {
                    onCreateAccount: _cache[8] || (_cache[8] = ($event: any) => (showAuthModal.value = true)),
                    onDownloadLimitReached: updateDownloadLimitInfo
                  }),
                  _createVNode(DownloadPanel, {
                    resolutions: _unref(getAvailableResolutions)().slice(0, 4),
                    "is-downloading": _unref(isDownloading),
                    "can-download": canDownload.value,
                    onDownload: downloadWallpaperWithAd
                  }, null, 8, ["resolutions", "is-downloading", "can-download"]),
                  _createVNode(MobileAd, {
                    "ad-type": "rectangle",
                    class: "mt-4"
                  })
                ]),
                _createElementVNode("div", _hoisted_31, [
                  _createVNode(DevicePreview, {
                    "device-type": _unref(mapToDevicePreviewType)(_unref(selectedDevice)),
                    "device-display-name": _unref(getDeviceDisplayName)(_unref(selectedDevice)),
                    "image-url": _unref(currentImageUrl),
                    "wallpaper-id": _unref(wallpaper)?.id || '',
                    dimensions: _unref(currentDimensions),
                    "aspect-ratio": _unref(calculateAspectRatio)(_unref(currentDimensions)),
                    "has-multiple-variations": _unref(wallpaper).isWallpaperPack && _unref(selectedDevice) && _unref(getDeviceVariations)(_unref(wallpaper), _unref(selectedDevice)).length > 1,
                    variations: _unref(wallpaper).isWallpaperPack && _unref(selectedDevice) ? _unref(getDeviceVariations)(_unref(wallpaper), _unref(selectedDevice)) : [],
                    "current-variation-index": _unref(currentVariationIndex),
                    "total-variations": _unref(wallpaper).isWallpaperPack && _unref(selectedDevice) ? _unref(getDeviceVariations)(_unref(wallpaper), _unref(selectedDevice)).length : 0,
                    "fit-option": selectedFitOption.value,
                    "is-loading": false,
                    "is-live-wallpaper": _unref(wallpaper)?.isVideo || _unref(wallpaper)?.isAnimated,
                    "is-video": _unref(wallpaper)?.isVideo,
                    "is-animated": _unref(wallpaper)?.isAnimated,
                    "video-url": _unref(wallpaper)?.videoUrl,
                    onFullscreen: _cache[9] || (_cache[9] = ($event: any) => (showImageViewer.value = true)),
                    onShare: _unref(shareWallpaper),
                    onPreviewClick: _cache[10] || (_cache[10] = ($event: any) => (showImageViewer.value = true)),
                    onTouchStart: _unref(handleTouchStart),
                    onTouchMove: _unref(handleTouchMove),
                    onTouchEnd: _unref(handleTouchEnd),
                    onSelectVariation: onSelectVariationDesktop,
                    onFitChanged: handleFitChanged
                  }, null, 8, ["device-type", "device-display-name", "image-url", "wallpaper-id", "dimensions", "aspect-ratio", "has-multiple-variations", "variations", "current-variation-index", "total-variations", "fit-option", "is-live-wallpaper", "is-video", "is-animated", "video-url", "onShare", "onTouchStart", "onTouchMove", "onTouchEnd"]),
                  (_unref(wallpaper).description)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_32, [
                        _cache[23] || (_cache[23] = _createElementVNode("h3", { class: "text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" }, "Description", -1)),
                        _createElementVNode("p", _hoisted_33, _toDisplayString(_unref(wallpaper).description), 1)
                      ]))
                    : _createCommentVNode("", true),
                  (_unref(wallpaper).tags && _unref(wallpaper).tags.length > 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_34, [
                        _cache[24] || (_cache[24] = _createElementVNode("h3", { class: "text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" }, "Tags", -1)),
                        _createElementVNode("div", _hoisted_35, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(wallpaper).tags, (tag) => {
                            return (_openBlock(), _createElementBlock("span", {
                              key: tag,
                              class: "inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors cursor-pointer"
                            }, " #" + _toDisplayString(tag), 1))
                          }), 128))
                        ])
                      ]))
                    : _createCommentVNode("", true)
                ])
              ])
            ]))
          : _createCommentVNode("", true),
    _createVNode(DownloadNotification, {
      show: _unref(notification).show,
      type: _unref(notification).type,
      message: _unref(notification).message,
      resolution: _unref(notification).resolution,
      onClose: _unref(hideNotification)
    }, null, 8, ["show", "type", "message", "resolution", "onClose"]),
    _createVNode(InterstitialAdModal, {
      "is-visible": showInterstitialAd.value,
      "download-info": pendingDownload.value,
      onDownload: handleInterstitialDownload,
      onClose: _cache[11] || (_cache[11] = ($event: any) => (showInterstitialAd.value = false)),
      onSkip: handleInterstitialSkip
    }, null, 8, ["is-visible", "download-info"]),
    _createVNode(MobileInterstitialAd, {
      ref_key: "mobileInterstitialRef",
      ref: mobileInterstitialRef,
      trigger: "download",
      frequency: 2
    }, null, 512),
    _createVNode(AuthModal, {
      "is-open": showAuthModal.value,
      onClose: _cache[12] || (_cache[12] = ($event: any) => (showAuthModal.value = false)),
      onSuccess: handleAuthSuccess
    }, null, 8, ["is-open"])
  ]))
}
}

})